import { Divider, Flex, Text } from '@chakra-ui/react';
import { Accordion, ButtonText } from '@morressier/alembic';
import { useParams } from 'react-router-dom';

import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { SELECTED_ITEM_QUERY_KEY } from 'src/constants';

import withAuthorizedComponent from 'src/components/AuthorizedComponent';
import {
  faBell,
  faCheck,
  faEnvelope,
  faFileImport,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import { IconBadge } from 'src/components/IconBadge';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import HighlightItem from '../PeerReview/HighlightItem';
import { useQuery } from '@tanstack/react-query';
import { fetchExpertsInviteDetails } from 'src/api/queries';
import { SUBMISSION_DETAIL_TABS } from 'src/pages/SubmissionDetails';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import ProtectedWorkflowAction from '../../../../ProtectedAction/ProtectedWorkflowAction';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';

interface Props {
  setActiveTab: (tab: any) => JSX.Element;
}
const InitialAssessmentAdditionalInfo = ({ setActiveTab }: Props) => {
  // hooks
  const { workflowResponse, submissionContextId } = useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const { setReviewsSubTab } = useSubmissionDetails();
  const { submissionId: paramId = '' } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const selectedItem = searchParams.get(SELECTED_ITEM_QUERY_KEY);
  const submissionId = selectedItem || paramId;
  const { data } = useQuery({
    queryKey: [
      'board-member-experts-invite-list',
      { submissionContextId, submissionId }
    ],
    queryFn: () =>
      submissionId && submissionContextId
        ? fetchExpertsInviteDetails(
            submissionContextId,
            submissionId,
            'early-assessments'
          )
        : null
  });

  const isWithdrawalRequested =
    !!workflowResponse.workflowData.withdrawal?.requested;
  const isUpdatedByAuthor =
    workflowResponse.workflowData.previousState === 'immediateRevision';

  const showHighlights = isUpdatedByAuthor || isWithdrawalRequested;
  const initialAssessmentStatus =
    workflowResponse.workflowData.currentState === 'initialAssessment';

  const ShowHighlightItem = () => {
    if (submissionContext?.config.isEarlyAssessmentEnabled) {
      return true;
    }
    if (showHighlights) {
      return true;
    }
    return false;
  };

  return (
    <AdditionalInformationWrapper>
      <Flex gap={2} flexDirection="column" width={'100%'}>
        {ShowHighlightItem() && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              defaultIndex: 0,
              allowToggle: true
            }}
            items={[
              {
                title: (
                  <Flex alignItems={'flex-start'} gap={2}>
                    <IconBadge icon={faBell} variant="neutral" />
                    <Flex flexDirection={'column'} marginLeft={2}>
                      <Text textStyle="headingItem">Highlights</Text>
                      <Text textStyle="textContext">
                        Review key updates and other relevant information in
                        this phase.
                      </Text>
                    </Flex>
                  </Flex>
                ),
                content:
                  submissionContext?.config.isEarlyAssessmentEnabled &&
                  initialAssessmentStatus &&
                  !showHighlights ? (
                    <Flex flexDirection="column" width="100%" gap={4}>
                      <HighlightItem
                        variant="attention"
                        icon={faEnvelope}
                        heading={`${data?.total ?? '0'} Experts Invited`}
                        context="Experts can be brought in to provide a recommendation
                   on the submission's suitability."
                        customButton={
                          <ProtectedWorkflowAction actionName="RequestEarlyAssessment">
                            <ButtonText
                              flexShrink="0"
                              onClick={() => {
                                setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS);
                                setReviewsSubTab('Early Assessments');
                              }}
                            >
                              Invite Experts
                            </ButtonText>
                          </ProtectedWorkflowAction>
                        }
                      />
                      <Divider />
                      {data && data?.total !== 0 && (
                        <>
                          {data?.completedAssessment > 0 && (
                            <HighlightItem
                              variant="success"
                              icon={faCheck}
                              heading={`${
                                data?.completedAssessment ?? '0'
                              } Early Assessment Done`}
                              buttonOnClick={() =>
                                setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS)
                              }
                              buttonText="To Reviews"
                            />
                          )}
                          {data?.declinedOrExpired > 0 && (
                            <>
                              <Divider />
                              <HighlightItem
                                variant="caution"
                                icon={faWarning}
                                heading={'Deadline Alerts'}
                                context={`0 Deadline in Early Assessment needs your attention`}
                                buttonOnClick={() =>
                                  setActiveTab(SUBMISSION_DETAIL_TABS.REVIEWS)
                                }
                                buttonText="To Reviews"
                              />
                            </>
                          )}
                        </>
                      )}
                    </Flex>
                  ) : (
                    <Flex flexDirection="column" width="100%" gap={4}>
                      {isUpdatedByAuthor && (
                        <HighlightItem
                          variant="attention"
                          icon={faFileImport}
                          context="Updates are now available to review in the
                       “Submission” and “About” tabs."
                          heading="Submission Updated by Author"
                        />
                      )}
                      <EditorialAuthorWithdrawalRequested />
                    </Flex>
                  )
              }
            ]}
          />
        )}
      </Flex>
    </AdditionalInformationWrapper>
  );
};

export default withAuthorizedComponent(InitialAssessmentAdditionalInfo, {
  relation: 'task_completer',
  entityType: 'journal_phase',
  getEntityId: () => `${useCurrentContextId() ?? ''}_initialAssessment`
});
